import { useEffect, useMemo, useState } from 'react';
import { useAwmsStore } from '@/store';
import {
  useHandleError,
  useCampusList,
  useUnitWorkloadDetails,
  useUnitWorkloadAcademics,
  useUnitWorkloadCampuses,
  useTeachingDeliveryDetails,
  useUnitWorkloadSchool,
} from '@/hooks';

export const useUnitWorkloadData = (uwId: number) => {
  const handleError = useHandleError();
  const activeYear = useAwmsStore((state) => state.activeYear);
  const selectedCampusId = useAwmsStore((state) => state.selectedCampusId);
  const setSelectedCampusId = useAwmsStore((state) => state.setSelectedCampusId);
  const [isPageLoading, setIsPageLoading] = useState(true);

  const {
    data: unitWorkloadSchool,
    isLoading: isUnitWorkloadSchoolLoading,
    error: unitWorkloadSchoolError,
  } = useUnitWorkloadSchool(uwId);

  const {
    data: unitWorkloadDetails,
    isLoading: isUnitWorkloadDetailsLoading,
    error: unitWorkloadDetailsError,
  } = useUnitWorkloadDetails(uwId, activeYear, unitWorkloadSchool?.id ?? 0);

  const {
    data: unitWorkloadCampuses,
    isLoading: isUnitWorkloadCampusesLoading,
    error: unitWorkloadCampusesError,
  } = useUnitWorkloadCampuses(uwId, activeYear);

  const {
    data: assignedAcademics,
    isLoading: isAssignedAcademicsLoading,
    error: assignedAcademicsError,
  } = useUnitWorkloadAcademics(uwId);

  const sortedAssignedAcademics = useMemo(() => {
    if (!assignedAcademics) return [];

    return [...assignedAcademics].sort(
      (a, b) =>
        a.academicWorkload.lastName.localeCompare(b.academicWorkload.lastName) ||
        a.academicWorkload.firstName.localeCompare(b.academicWorkload.firstName),
    );
  }, [assignedAcademics]);

  const { data: campusList, isLoading: isCampusListLoading, error: campusListError } = useCampusList(activeYear);

  const {
    data: unitTeachingDeliveryDetails,
    isLoading: isUnitTeachingDeliveryDetailsLoading,
    error: unitTeachingDeliveryDetailsError,
  } = useTeachingDeliveryDetails(uwId, selectedCampusId);

  useEffect(() => {
    if (!selectedCampusId && unitWorkloadCampuses) {
      const defaultCampusId = unitWorkloadCampuses.length > 0 ? unitWorkloadCampuses[0]!.campusId : 0;
      setSelectedCampusId(defaultCampusId);
    }
  }, [selectedCampusId, unitWorkloadCampuses]);

  useEffect(() => {
    setIsPageLoading(
      isUnitWorkloadDetailsLoading ||
        isUnitWorkloadCampusesLoading ||
        isAssignedAcademicsLoading ||
        isCampusListLoading ||
        isUnitTeachingDeliveryDetailsLoading ||
        isUnitWorkloadSchoolLoading,
    );
  }, [
    isAssignedAcademicsLoading,
    isCampusListLoading,
    isUnitWorkloadCampusesLoading,
    isUnitWorkloadDetailsLoading,
    isUnitTeachingDeliveryDetailsLoading,
    isUnitWorkloadSchoolLoading,
  ]);

  useEffect(() => {
    if (
      assignedAcademicsError ||
      campusListError ||
      unitWorkloadDetailsError ||
      unitWorkloadCampusesError ||
      unitTeachingDeliveryDetailsError ||
      unitWorkloadSchoolError
    ) {
      console.error('Error fetching data:', {
        assignedAcademicsError,
        campusListError,
        unitWorkloadDetailsError,
        unitWorkloadCampusesError,
        unitTeachingDeliveryDetailsError,
        unitWorkloadSchoolError,
      });
    }
    handleError([
      assignedAcademicsError,
      campusListError,
      unitWorkloadDetailsError,
      unitWorkloadCampusesError,
      unitTeachingDeliveryDetailsError,
      unitWorkloadSchoolError,
    ]);
  }, [
    assignedAcademicsError,
    campusListError,
    unitWorkloadDetailsError,
    unitWorkloadCampusesError,
    unitTeachingDeliveryDetailsError,
    unitWorkloadSchoolError,
    handleError,
  ]);

  return {
    assignedAcademics,
    sortedAssignedAcademics,
    campusList,
    unitWorkloadDetails,
    unitWorkloadCampuses,
    unitTeachingDeliveryDetails,
    isPageLoading,
  };
};
